import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  BarChart3,
  Search,
  AlertCircle,
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
  Users2,
  Mic,
} from 'lucide-react';
import { getSessions, getUsers, lookupUsers } from '../../services/api';
import {
  listTasks,
  listAssignments,
  listTranscriberSupervisorAssignments,
  listTranscriberAssignments,
} from '../../services/annotationApi';
import { UserEmail } from '../../components/common/UserEmail';
import { useCurrentUser } from '../../context/UserContext';
import { formatHm } from '../../utils/formatDuration';

// ─── Types ─────────────────────────────────────────────────────────────────────

interface UserRecord {
  userId: string;
  displayName?: string;
  email?: string;
  role?: string;
  approvalStatus?: string;
}

interface Session {
  sessionId: string;
  enumeratorId: string;
  duration?: number;
  createdAt: string;
  status?: string;
}

// list-tasks returns t.* with these camel-cased; declare what we read locally.
interface TaskRow {
  taskId: string;
  enumeratorId: string;
  submittedBy?: string | null;
  claimedBy?: string | null;
  status: string;
  duration?: number | null;
  submittedAt?: string | null;
  reviewedAt?: string | null;
  createdAt: string;
}

type PresetKey = '24h' | '7d' | '30d' | 'all';
type DateMode = 'preset' | 'custom';
interface DateFilter {
  mode: DateMode;
  presetKey: PresetKey;
  from?: string;
  to?: string;
}

// Same column set for both tables — see plan.
type SortKey =
  | 'name'
  | 'videosUploaded'
  | 'minutesUploaded'
  | 'videosTranscribed'
  | 'minutesTranscribed'
  | 'videosClaimed'
  | 'videosApproved'
  | 'minutesApproved';

type SortDir = 'asc' | 'desc';

interface MetricsRow {
  user: UserRecord;
  videosUploaded: number;
  minutesUploaded: number;
  videosTranscribed: number;
  minutesTranscribed: number;
  videosClaimed: number;
  videosApproved: number;
  minutesApproved: number;
}

// ─── Date filter helpers ───────────────────────────────────────────────────────
// Same primitives as TimeboundAnalyticsPage; kept inline to avoid a premature
// shared module while only two pages need them.

function presetRange(key: PresetKey): { from?: Date; to?: Date } {
  if (key === 'all') return {};
  const now = new Date();
  const from = new Date(now);
  if (key === '24h') from.setHours(now.getHours() - 24);
  else if (key === '7d') from.setDate(now.getDate() - 7);
  else if (key === '30d') from.setDate(now.getDate() - 30);
  return { from, to: now };
}

function customRange(filter: DateFilter): { from?: Date; to?: Date } {
  const from = filter.from ? new Date(`${filter.from}T00:00:00`) : undefined;
  const to = filter.to ? new Date(`${filter.to}T23:59:59.999`) : undefined;
  return { from, to };
}

function activeRange(filter: DateFilter) {
  return filter.mode === 'preset' ? presetRange(filter.presetKey) : customRange(filter);
}

function inRange(iso: string | null | undefined, from?: Date, to?: Date): boolean {
  if (!iso) return false;
  const t = new Date(iso).getTime();
  if (from && t < from.getTime()) return false;
  if (to && t > to.getTime()) return false;
  return true;
}

function presetLabel(key: PresetKey): string {
  return key === '24h' ? 'Last 24h'
    : key === '7d' ? 'Last 7d'
    : key === '30d' ? 'Last 30d'
    : 'All time';
}

// ─── Sort header ───────────────────────────────────────────────────────────────

function SortHeader({
  label,
  sortKey,
  current,
  dir,
  onSort,
  align = 'left',
}: {
  label: string;
  sortKey: SortKey;
  current: SortKey;
  dir: SortDir;
  onSort: (key: SortKey) => void;
  align?: 'left' | 'right';
}) {
  const active = current === sortKey;
  const Icon = !active ? ChevronsUpDown : dir === 'desc' ? ChevronDown : ChevronUp;
  return (
    <th
      className={`px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide cursor-pointer select-none hover:text-gray-700 ${
        align === 'right' ? 'text-right' : 'text-left'
      }`}
      onClick={() => onSort(sortKey)}
    >
      <span className={`inline-flex items-center gap-1 ${align === 'right' ? 'justify-end' : ''}`}>
        {label}
        <Icon size={12} className={active ? 'text-violet-600' : 'text-gray-400'} />
      </span>
    </th>
  );
}

function defaultDirFor(k: SortKey): SortDir {
  return k === 'name' ? 'asc' : 'desc';
}

// ─── Per-user metrics computation ──────────────────────────────────────────────

const TERMINAL_OR_REJECTED = new Set([
  'approved',
  'final_accepted',
  'final_rejected',
  'rejected',
]);
// Mirror backend services/metricDefinitions.ts so "transcribed/submitted" here
// means the same thing as everywhere else (excludes rejected/final_rejected).
const SUBMITTED_STATUSES = new Set(['submitted', 'revision_needed', 'approved', 'final_accepted']);
const APPROVED_STATUSES = new Set(['approved', 'final_accepted']);

// Pre-built lookup indexes — built ONCE per (sessions, tasks) snapshot and
// reused across every user. Reduces per-user work from O(s + t) to O(per-user
// bucket size), which is ~constant on average. The page-wide aggregation
// drops from O(users × (sessions + tasks)) to O(sessions + tasks + users).
interface MetricsIndexes {
  sessionsByEnumerator: Map<string, Session[]>;
  tasksBySubmittedBy: Map<string, TaskRow[]>;
  tasksByClaimedBy: Map<string, TaskRow[]>;
}

function buildMetricsIndexes(sessions: Session[], tasks: TaskRow[]): MetricsIndexes {
  const sessionsByEnumerator = new Map<string, Session[]>();
  for (const s of sessions) {
    if (!s.enumeratorId) continue;
    const arr = sessionsByEnumerator.get(s.enumeratorId);
    if (arr) arr.push(s); else sessionsByEnumerator.set(s.enumeratorId, [s]);
  }
  const tasksBySubmittedBy = new Map<string, TaskRow[]>();
  const tasksByClaimedBy = new Map<string, TaskRow[]>();
  for (const t of tasks) {
    if (t.submittedBy) {
      const arr = tasksBySubmittedBy.get(t.submittedBy);
      if (arr) arr.push(t); else tasksBySubmittedBy.set(t.submittedBy, [t]);
    }
    if (t.claimedBy) {
      const arr = tasksByClaimedBy.get(t.claimedBy);
      if (arr) arr.push(t); else tasksByClaimedBy.set(t.claimedBy, [t]);
    }
  }
  return { sessionsByEnumerator, tasksBySubmittedBy, tasksByClaimedBy };
}

function computeMetrics(
  user: UserRecord,
  indexes: MetricsIndexes,
  from?: Date,
  to?: Date,
): MetricsRow {
  let videosUploaded = 0;
  let secondsUploaded = 0;
  for (const s of indexes.sessionsByEnumerator.get(user.userId) ?? []) {
    // Only completed sessions are real uploads — drafts/uploading/failed are
    // incomplete and would inflate the count vs. the review queue.
    if (s.status !== 'completed') continue;
    if (!inRange(s.createdAt, from, to)) continue;
    videosUploaded += 1;
    secondsUploaded += s.duration ?? 0;
  }

  let videosTranscribed = 0;
  let secondsTranscribed = 0;
  let videosApproved = 0;
  let secondsApproved = 0;
  for (const t of indexes.tasksBySubmittedBy.get(user.userId) ?? []) {
    if (SUBMITTED_STATUSES.has(t.status) && inRange(t.submittedAt, from, to)) {
      videosTranscribed += 1;
      secondsTranscribed += t.duration ?? 0;
    }
    if (APPROVED_STATUSES.has(t.status) && inRange(t.reviewedAt, from, to)) {
      videosApproved += 1;
      secondsApproved += t.duration ?? 0;
    }
  }

  // Claimed = snapshot; ignores date filter on purpose.
  let videosClaimed = 0;
  for (const t of indexes.tasksByClaimedBy.get(user.userId) ?? []) {
    if (!TERMINAL_OR_REJECTED.has(t.status)) videosClaimed += 1;
  }

  return {
    user,
    videosUploaded,
    minutesUploaded: secondsUploaded / 60,
    videosTranscribed,
    minutesTranscribed: secondsTranscribed / 60,
    videosClaimed,
    videosApproved,
    minutesApproved: secondsApproved / 60,
  };
}

// ─── Metric table ──────────────────────────────────────────────────────────────

function MetricTable({
  title,
  icon,
  rows,
  search,
  defaultSort,
}: {
  title: string;
  icon: React.ReactNode;
  rows: MetricsRow[];
  search: string;
  defaultSort: SortKey;
}) {
  const [sort, setSort] = useState<{ key: SortKey; dir: SortDir }>({
    key: defaultSort,
    dir: 'desc',
  });

  const onSort = (key: SortKey) =>
    setSort((prev) => {
      if (prev.key === key) return { key, dir: prev.dir === 'desc' ? 'asc' : 'desc' };
      return { key, dir: defaultDirFor(key) };
    });

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        (r.user.displayName ?? '').toLowerCase().includes(q) ||
        (r.user.email ?? '').toLowerCase().includes(q),
    );
  }, [rows, search]);

  const sorted = useMemo(() => {
    const factor = sort.dir === 'desc' ? -1 : 1;
    const arr = [...filtered];
    arr.sort((a, b) => {
      let cmp = 0;
      switch (sort.key) {
        case 'name':
          cmp = (a.user.displayName ?? '').localeCompare(b.user.displayName ?? '');
          break;
        case 'videosUploaded':     cmp = a.videosUploaded     - b.videosUploaded; break;
        case 'minutesUploaded':    cmp = a.minutesUploaded    - b.minutesUploaded; break;
        case 'videosTranscribed':  cmp = a.videosTranscribed  - b.videosTranscribed; break;
        case 'minutesTranscribed': cmp = a.minutesTranscribed - b.minutesTranscribed; break;
        case 'videosClaimed':      cmp = a.videosClaimed      - b.videosClaimed; break;
        case 'videosApproved':     cmp = a.videosApproved     - b.videosApproved; break;
        case 'minutesApproved':    cmp = a.minutesApproved    - b.minutesApproved; break;
      }
      if (cmp === 0) cmp = (a.user.displayName ?? '').localeCompare(b.user.displayName ?? '');
      return cmp * factor;
    });
    return arr;
  }, [filtered, sort]);

  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
      <div className="px-5 py-3 border-b border-gray-200 bg-gray-50 flex items-center gap-2">
        {icon}
        <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
          {title} ({rows.length})
        </h2>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-white border-b border-gray-200">
            <tr>
              <SortHeader label="User"               sortKey="name"               current={sort.key} dir={sort.dir} onSort={onSort} />
              <SortHeader label="Videos Uploaded"    sortKey="videosUploaded"     current={sort.key} dir={sort.dir} onSort={onSort} align="right" />
              <SortHeader label="Uploaded hrs"   sortKey="minutesUploaded"    current={sort.key} dir={sort.dir} onSort={onSort} align="right" />
              <SortHeader label="Videos Transcribed" sortKey="videosTranscribed"  current={sort.key} dir={sort.dir} onSort={onSort} align="right" />
              <SortHeader label="Transcribed hrs"    sortKey="minutesTranscribed" current={sort.key} dir={sort.dir} onSort={onSort} align="right" />
              <SortHeader label="Videos Claimed"     sortKey="videosClaimed"      current={sort.key} dir={sort.dir} onSort={onSort} align="right" />
              <SortHeader label="Videos Approved"    sortKey="videosApproved"     current={sort.key} dir={sort.dir} onSort={onSort} align="right" />
              <SortHeader label="Approved hrs"       sortKey="minutesApproved"    current={sort.key} dir={sort.dir} onSort={onSort} align="right" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {sorted.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-4 py-8 text-center text-gray-400 text-sm">
                  No users in this section.
                </td>
              </tr>
            ) : (
              sorted.map((r) => (
                <tr key={r.user.userId} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="text-sm font-medium text-gray-900">
                      {r.user.displayName ?? '—'}
                    </div>
                    {r.user.email && (
                      <UserEmail email={r.user.email} role={r.user.role ?? ''} />
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-right text-gray-700">{r.videosUploaded}</td>
                  <td className="px-4 py-3 text-sm text-right text-gray-700">{formatHm(r.minutesUploaded * 60)}</td>
                  <td className="px-4 py-3 text-sm text-right text-gray-700">{r.videosTranscribed}</td>
                  <td className="px-4 py-3 text-sm text-right text-gray-700">{formatHm(r.minutesTranscribed * 60)}</td>
                  <td className="px-4 py-3 text-sm text-right text-gray-700">{r.videosClaimed}</td>
                  <td className="px-4 py-3 text-sm text-right text-emerald-700 font-medium">{r.videosApproved}</td>
                  <td className="px-4 py-3 text-sm text-right text-emerald-700 font-medium">{formatHm(r.minutesApproved * 60)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function AnnotationAnalyticsPage() {
  const { role, userId: meId } = useCurrentUser();

  const [dateFilter, setDateFilter] = useState<DateFilter>({ mode: 'preset', presetKey: 'all' });
  const [search, setSearch] = useState('');

  const setPreset = (presetKey: PresetKey) =>
    setDateFilter({ mode: 'preset', presetKey, from: undefined, to: undefined });
  const setCustomFrom = (from: string) =>
    setDateFilter((prev) => ({ mode: 'custom', presetKey: 'all', from, to: prev.to }));
  const setCustomTo = (to: string) =>
    setDateFilter((prev) => ({ mode: 'custom', presetKey: 'all', from: prev.from, to }));

  const allowed = role === 'admin' || role === 'supervisor';
  const isAdmin = role === 'admin';

  // ── Common data: tasks (role-scoped server-side) + sessions ──────────────
  const { data: tasksData, isLoading: tasksLoading, error: tasksError } = useQuery({
    queryKey: ['analytics-tasks', role],
    // listTasks now returns { tasks, total, limit, offset }. This page does
    // a full aggregation across tasks, so request a high limit and unwrap.
    queryFn: () => listTasks({ limit: 5000 }).then((r) => r.tasks),
    enabled: allowed,
  });
  const tasks: TaskRow[] = (tasksData ?? []) as unknown as TaskRow[];

  const { data: sessionsData, isLoading: sessionsLoading, error: sessionsError } = useQuery({
    queryKey: ['analytics-sessions'],
    queryFn: () => getSessions({ limit: '5000' }).then((d) => d.sessions ?? d ?? []),
    enabled: allowed,
  });
  const sessions: Session[] = sessionsData ?? [];

  // ── User set in scope ────────────────────────────────────────────────────
  // Admin: all approved users.
  const { data: adminUsersData } = useQuery({
    queryKey: ['analytics-all-users'],
    queryFn: () => getUsers({ approvalStatus: 'approved', limit: '5000' }),
    enabled: allowed && isAdmin,
  });
  const adminUsers: UserRecord[] = adminUsersData?.users ?? [];

  // Supervisor: derive subtree from assignment tables, then bulk-resolve names.
  const { data: supAssignments = [] } = useQuery({
    queryKey: ['analytics-my-supervisor-assignments', meId],
    queryFn: () => listAssignments({ supervisorId: meId! }),
    enabled: allowed && !isAdmin && !!meId,
  });
  const { data: tsupAssignments = [] } = useQuery({
    queryKey: ['analytics-my-transcriber-supervisor-assignments', meId],
    queryFn: () => listTranscriberSupervisorAssignments({ supervisorId: meId! }),
    enabled: allowed && !isAdmin && !!meId,
  });
  // Bulk fetch of all transcriber→enumerator links in the supervisor's subtree.
  // Backend joins via transcriber_supervisor_assignments; one round-trip instead
  // of one per transcriber.
  const { data: subtreeTranscriberAssignments = [] } = useQuery({
    queryKey: ['analytics-transcriber-assignments-subtree', meId],
    enabled: !isAdmin && !!meId,
    queryFn: () => listTranscriberAssignments({ supervisorId: meId! }),
  });
  const supervisorReferencedIds = useMemo(() => {
    const s = new Set<string>();
    for (const a of supAssignments) s.add(a.enumeratorId);
    for (const a of tsupAssignments) s.add(a.transcriberId);
    for (const a of subtreeTranscriberAssignments) s.add(a.enumeratorId);
    return [...s];
  }, [supAssignments, tsupAssignments, subtreeTranscriberAssignments]);
  const { data: lookupData } = useQuery({
    queryKey: ['analytics-users-lookup', supervisorReferencedIds],
    enabled: !isAdmin && supervisorReferencedIds.length > 0,
    queryFn: () => lookupUsers(supervisorReferencedIds),
  });
  const supervisorUsers: UserRecord[] = lookupData?.users ?? [];

  const usersInScope: UserRecord[] = isAdmin ? adminUsers : supervisorUsers;

  // ── Aggregate per user ───────────────────────────────────────────────────
  const range = useMemo(() => activeRange(dateFilter), [dateFilter]);

  // Build the lookup indexes once per (sessions, tasks) change. The date
  // filter only affects the per-user iteration, not the index, so we don't
  // rebuild on every chip toggle.
  const indexes = useMemo(() => buildMetricsIndexes(sessions, tasks), [sessions, tasks]);

  const allRows = useMemo<MetricsRow[]>(() => {
    if (!usersInScope.length) return [];
    return usersInScope.map((u) => computeMetrics(u, indexes, range.from, range.to));
  }, [usersInScope, indexes, range]);

  const enumeratorRows = useMemo(
    () => allRows.filter((r) => r.user.role === 'enumerator'),
    [allRows],
  );
  const transcriberRows = useMemo(
    () => allRows.filter((r) => r.user.role === 'transcriber'),
    [allRows],
  );

  // ── Render ───────────────────────────────────────────────────────────────
  if (!allowed) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3">
        <AlertCircle className="w-10 h-10 text-gray-400" />
        <p className="text-gray-600 font-medium">You do not have access to this page.</p>
      </div>
    );
  }

  const loading = tasksLoading || sessionsLoading;
  const hasError = tasksError || sessionsError;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-violet-100 rounded-xl flex items-center justify-center">
          <BarChart3 className="w-5 h-5 text-violet-600" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Analytics</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Per-user upload &amp; transcription work, split by role. "Videos Claimed" is a snapshot;
            other metrics respect the selected date window.
          </p>
        </div>
      </div>

      {/* Filter bar */}
      <div className="bg-white border border-gray-200 rounded-xl p-4 flex flex-col lg:flex-row gap-4 lg:items-center lg:justify-between">
        <div className="flex flex-wrap items-center gap-2">
          {(['24h', '7d', '30d', 'all'] as PresetKey[]).map((k) => {
            const active = dateFilter.mode === 'preset' && dateFilter.presetKey === k;
            return (
              <button
                key={k}
                onClick={() => setPreset(k)}
                className={`px-3 py-1.5 text-sm rounded-lg font-medium transition-colors ${
                  active
                    ? 'bg-violet-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {presetLabel(k)}
              </button>
            );
          })}
          <div className="flex items-center gap-2 ml-2 pl-2 border-l border-gray-200">
            <input
              type="date"
              value={dateFilter.mode === 'custom' ? dateFilter.from ?? '' : ''}
              onChange={(e) => setCustomFrom(e.target.value)}
              className="px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
            />
            <span className="text-gray-400 text-sm">to</span>
            <input
              type="date"
              value={dateFilter.mode === 'custom' ? dateFilter.to ?? '' : ''}
              onChange={(e) => setCustomTo(e.target.value)}
              className="px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
            />
          </div>
        </div>

        <div className="relative w-full lg:w-80">
          <Search
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
          />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Filter by name or email"
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
          />
        </div>
      </div>

      {hasError && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3 text-sm text-red-700">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span>Failed to load analytics data. Reload the page to retry.</span>
        </div>
      )}

      {loading ? (
        <div className="bg-white border border-gray-200 rounded-xl p-12 text-center text-gray-400 text-sm">
          Loading analytics…
        </div>
      ) : (
        <>
          <MetricTable
            title="Enumerators"
            icon={<Users2 size={16} className="text-violet-600" />}
            rows={enumeratorRows}
            search={search}
            defaultSort="videosUploaded"
          />
          <MetricTable
            title="Transcribers"
            icon={<Mic size={16} className="text-sky-600" />}
            rows={transcriberRows}
            search={search}
            defaultSort="videosTranscribed"
          />
        </>
      )}
    </div>
  );
}
